import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Checkbox,
} from 'antd';
import type { ModalProps } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { withDevModalFilePath } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/BankTransferManagementPage.tsx');
import {
  CheckCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  MailOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  AuditOutlined,
  PhoneOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { CreateBankTransferTestModal } from './components/CreateBankTransferTestModal';
import dayjs from 'dayjs';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import { formatCurrency } from './subscription-helpers';
import type { BankTransferStatus } from '../../../types/company';
import { useNavigate } from 'react-router-dom';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Option } = Select;
const { Text } = Typography;

type BankTransferContractRow = {
  id: string;
  subscriptionId: string;
  planCode: string;
  status: 'pending_payment' | 'active' | 'overdue' | string;
  currency: string;
  amount: number;
  company: {
    id: string;
    name: string;
    email: string | null;
    bankTransferStatus: BankTransferStatus | null;
    bankTransferConfirmedAt: string | null;
  };
  paymentDueDate?: string | null;
  nextInvoiceDate?: string | null;
  latestInvoiceId?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, unknown> | null;
};

interface ContractsResponse {
  items: BankTransferContractRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

type ReminderMethod = 'email' | 'phone' | 'other';
type BankTransferFilterStatus = 'all' | 'pending_payment' | 'overdue' | 'active' | undefined;
type ContractsQueryInput = {
  status?: Exclude<BankTransferFilterStatus, undefined | 'all'>;
  search?: string;
  page: number;
  limit: number;
  sortBy: 'dueDate' | 'updatedAt' | 'amount';
  sortOrder: 'asc' | 'desc';
};
type ContractsQueryState = {
  data?: ContractsResponse;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
};

type TestableButtonProps = NonNullable<ModalProps['okButtonProps']> & {
  'data-testid'?: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
};

const STATUS_LABELS: Record<string, string> = {
  pending_payment: '入金待ち',
  active: '入金済み',
  overdue: '期限切れ',
};

const STATUS_COLORS: Record<string, string> = {
  pending_payment: 'warning',
  active: 'success',
  overdue: 'error',
};

const PLAN_LABELS: Record<string, string> = {
  starter: 'スターター',
  standard: 'スタンダード',
  professional: 'プロフェッショナル',
  enterprise: 'エンタープライズ',
  enterprise_premium: 'エンタープライズ・プレミアム',
};

const STATUS_OPTIONS = [
  { value: undefined, label: 'すべて' },
  { value: 'pending_payment', label: '入金待ち' },
  { value: 'overdue', label: '期限切れ' },
  { value: 'active', label: '入金済み' },
];

const REMINDER_METHOD_OPTIONS: { value: ReminderMethod; label: string; icon: React.ReactNode }[] = [
  { value: 'email', label: 'メール', icon: <MailOutlined /> },
  { value: 'phone', label: '電話', icon: <PhoneOutlined /> },
  { value: 'other', label: 'その他', icon: <AuditOutlined /> },
];

function formatDate(value?: string | null) {
  if (!value) {
    return '-';
  }
  const d = dayjs(value);
  if (!d.isValid()) {
    return '-';
  }
  return d.format('YYYY-MM-DD');
}

function formatCompanyContact(company?: BankTransferContractRow['company']) {
  if (!company) {
    return '-';
  }
  if (company.email) {
    return company.email;
  }
  return `ID: ${company.id}`;
}

function formatCompanyDisplay(company?: BankTransferContractRow['company']) {
  if (!company) {
    return '-';
  }
  const contact = formatCompanyContact(company);
  return contact === '-' ? company.name : `${company.name} (${contact})`;
}

const DEFAULT_PAGE_SIZE = 20;

const BankTransferManagementPage: React.FC = () => {
  const { message } = App.useApp();
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<BankTransferFilterStatus>('pending_payment');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<'dueDate' | 'updatedAt' | 'amount'>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [confirmModalState, setConfirmModalState] = useState<{
    open: boolean;
    contract?: BankTransferContractRow;
    note: string;
    markCompanyActive: boolean;
  }>({ open: false, note: '', markCompanyActive: true });

  const [createTestModalOpen, setCreateTestModalOpen] = useState(false);

  const [reminderModalState, setReminderModalState] = useState<{
    open: boolean;
    contract?: BankTransferContractRow;
    memo: string;
    method: ReminderMethod;
  }>({ open: false, memo: '', method: 'email' });

  const queryInput = useMemo<ContractsQueryInput>(
    () => ({
      status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
      search: searchKeyword.trim() ? searchKeyword.trim() : undefined,
      page,
      limit: pageSize,
      sortBy,
      sortOrder,
    }),
    [statusFilter, searchKeyword, page, pageSize, sortBy, sortOrder],
  );

  const contractsQuery = trpc.subscriptionAdmin.listBankTransferContracts.useQuery(queryInput, {
    placeholderData: keepPreviousData,
  }) as ContractsQueryState;

  const invalidateContracts = () => {
    if (utils?.subscriptionAdmin?.listBankTransferContracts?.invalidate) {
      return utils.subscriptionAdmin.listBankTransferContracts.invalidate(queryInput);
    }
    return contractsQuery.refetch();
  };

  const confirmPaymentMutation = trpc.subscriptionAdmin.confirmBankTransferPayment.useMutation({
    onMutate: async (input) => {
      await utils.subscriptionAdmin.listBankTransferContracts.cancel(queryInput);
      const previousData =
        utils.subscriptionAdmin.listBankTransferContracts.getData(queryInput) ??
        (contractsQuery.data as ContractsResponse | undefined);
      const contractId =
        input && typeof input === 'object' && 'contractId' in input
          ? (input as { contractId?: string }).contractId
          : undefined;

      if (previousData && contractId) {
        const nowIso = new Date().toISOString();
        const optimisticItems = previousData.items.map((item) => {
          if (item.id !== contractId) {
            return item;
          }
          return {
            ...item,
            status: 'active',
            updatedAt: nowIso,
            company: {
              ...item.company,
              bankTransferStatus: 'confirmed',
              bankTransferConfirmedAt: nowIso,
            },
          };
        });

        utils.subscriptionAdmin.listBankTransferContracts.setData(
          queryInput,
          {
            ...previousData,
            items: optimisticItems,
          },
        );
      }

      return { previousData } as { previousData?: ContractsResponse };
    },
    onSuccess: () => {
      message.success('入金確認を記録しました');
      setConfirmModalState({ open: false, note: '', markCompanyActive: true });
      void invalidateContracts();
    },
    onError: (error: unknown, _input, context) => {
      const previousData = (context as { previousData?: ContractsResponse } | undefined)?.previousData;
      if (previousData) {
        utils.subscriptionAdmin.listBankTransferContracts.setData(queryInput, previousData);
      }
      message.error(getErrorMessage(error, '入金確認に失敗しました'));
    },
  });

  const sendReminderMutation = trpc.subscriptionAdmin.sendPaymentReminder.useMutation({
    onSuccess: () => {
      message.success('督促を記録しました');
      setReminderModalState({ open: false, memo: '', method: 'email' });
      void invalidateContracts();
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, '督促の記録に失敗しました'));
    },
  });

  // 疑似支払い（テスト用）
  const simulatePaymentMutation = trpc.subscriptionAdmin.simulateBankTransferPayment.useMutation({
    onSuccess: (data) => {
      message.success(`疑似支払いを実行しました (Invoice: ${data.invoiceId})`);
      void invalidateContracts();
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, '疑似支払いに失敗しました'));
    },
  });

  const items = contractsQuery.data?.items ?? [];
  const pagination = contractsQuery.data?.pagination;

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    _: Record<string, React.Key[] | null>,
    sorter: SorterResult<BankTransferContractRow> | SorterResult<BankTransferContractRow>[],
  ) => {
    if (paginationConfig.current && paginationConfig.current !== page) {
      setPage(paginationConfig.current);
    }
    if (paginationConfig.pageSize && paginationConfig.pageSize !== pageSize) {
      setPageSize(paginationConfig.pageSize);
      setPage(1);
    }

    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (activeSorter && activeSorter.columnKey) {
      const columnKey = String(activeSorter.columnKey);
      let nextSortBy: 'dueDate' | 'updatedAt' | 'amount' = sortBy;
      if (columnKey === 'paymentDueDate') {
        nextSortBy = 'dueDate';
      } else if (columnKey === 'updatedAt') {
        nextSortBy = 'updatedAt';
      } else if (columnKey === 'amount') {
        nextSortBy = 'amount';
      }
      const nextSortOrder: 'asc' | 'desc' =
        activeSorter.order === 'descend' ? 'desc' : 'asc';
      if (nextSortBy !== sortBy || nextSortOrder !== sortOrder) {
        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
      }
    }
  };

  const columns: ColumnsType<BankTransferContractRow> = [
    {
      title: '会社名',
      dataIndex: ['company', 'name'],
      key: 'company',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.company?.name ?? '-'}</Text>
          <Text type="secondary">{formatCompanyContact(record.company)}</Text>
        </Space>
      ),
    },
    {
      title: 'プラン',
      dataIndex: 'planCode',
      key: 'planCode',
      render: (value: string) => PLAN_LABELS[value] ?? value,
    },
    {
      title: '請求額',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      render: (_value, record) => formatCurrency(record.amount ?? 0),
    },
    {
      title: '支払期限',
      dataIndex: 'paymentDueDate',
      key: 'paymentDueDate',
      sorter: true,
      render: (value: string | null | undefined) => formatDate(value),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>
          {STATUS_LABELS[status] ?? status}
        </Tag>
      ),
    },
    {
      title: '最終更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      render: (value: string | null | undefined) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_value, record) => (
        <Space>
          {(record.status === 'pending_payment' || record.status === 'overdue') && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                data-testid="confirm-payment-btn"
                loading={
                  confirmModalState.open &&
                  confirmModalState.contract?.id === record.id &&
                  getMutationPendingState(confirmPaymentMutation)
                }
                onClick={() =>
                  setConfirmModalState({
                    open: true,
                    contract: record,
                    note: '',
                    markCompanyActive: true,
                  })
                }
              >
                入金確認
              </Button>
              <Button
                size="small"
                icon={<ClockCircleOutlined />}
                loading={
                  reminderModalState.open &&
                  reminderModalState.contract?.id === record.id &&
                  getMutationPendingState(sendReminderMutation)
                }
                onClick={() =>
                  setReminderModalState({
                    open: true,
                    contract: record,
                    memo: '',
                    method: 'email',
                  })
                }
              >
                督促登録
              </Button>
              <Popconfirm
                title="疑似支払いを実行"
                description="Stripe API を呼び出して請求書を支払い済みにします。テスト環境専用の機能です。"
                onConfirm={() => simulatePaymentMutation.mutate({
                  contractId: record.id,
                  invoiceId: record.latestInvoiceId ?? undefined,
                })}
                okText="実行"
                cancelText="キャンセル"
                okButtonProps={{ loading: simulatePaymentMutation.isPending }}
              >
                <Button
                  size="small"
                  icon={<DollarOutlined />}
                  loading={simulatePaymentMutation.isPending}
                  data-testid="simulate-payment-btn"
                >
                  疑似支払い
                </Button>
              </Popconfirm>
            </>
          )}
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(adminPath(`companies/${record.company?.id ?? ''}`))}
            disabled={!record.company?.id}
          >
            詳細
          </Button>
        </Space>
      ),
    },
  ];

  const handleStatusChange = (value: string | undefined) => {
    let nextValue: BankTransferFilterStatus;
    switch (value) {
      case 'pending_payment':
      case 'overdue':
      case 'active':
      case 'all':
      case undefined:
        nextValue = value;
        break;
      default:
        nextValue = undefined;
        break;
    }
    setStatusFilter(nextValue);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    setSearchKeyword(value);
    setPage(1);
  };

  const handleReset = () => {
    setStatusFilter('pending_payment');
    setSearchInput('');
    setSearchKeyword('');
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSortBy('dueDate');
    setSortOrder('asc');
  };

  const tablePagination: TablePaginationConfig = {
    current: pagination?.page ?? page,
    pageSize: pagination?.limit ?? pageSize,
    total: pagination?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}件`,
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          銀行振込管理
        </Typography.Title>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateTestModalOpen(true)}
            data-testid="create-test-subscription-btn"
          >
            テスト契約作成
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => contractsQuery.refetch()}
            loading={contractsQuery.isFetching}
          >
            更新
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        message="銀行振込契約の入金確認と督促を管理します"
        description="入金待ちの契約を確認し、入金確認または督促履歴を記録できます。"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card>
        <Space wrap style={{ width: '100%' }}>
          <Select
            allowClear
            placeholder="ステータスで絞り込み"
            style={{ width: 220 }}
            data-testid="status-filter"
            value={statusFilter}
            onChange={handleStatusChange}
          >
            {STATUS_OPTIONS.map((option) => (
              <Option key={option.value ?? 'all'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Input.Search
            placeholder="会社名・サブスクIDで検索"
            allowClear
            enterButton={<SearchOutlined />}
            style={{ width: 280 }}
            value={searchInput}
            onChange={(event) => {
              const value = event.target.value;
              setSearchInput(value);
              if (!value) {
                setSearchKeyword('');
                setPage(1);
              }
            }}
            onSearch={handleSearch}
          />
          <Button onClick={handleReset}>リセット</Button>
        </Space>
      </Card>

      <Table<BankTransferContractRow>
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={contractsQuery.isLoading || contractsQuery.isFetching}
        onChange={handleTableChange}
        pagination={tablePagination}
      />

      <Modal
        title={confirmModalState.contract ? `${confirmModalState.contract.company.name} の入金確認` : '入金確認'}
        open={confirmModalState.open}
        onCancel={() => setConfirmModalState({ open: false, note: '', markCompanyActive: true })}
        onOk={() => {
          if (!confirmModalState.contract) {
            return;
          }
          confirmPaymentMutation.mutate({
            contractId: confirmModalState.contract.id,
            note: confirmModalState.note || undefined,
            markCompanyActive: confirmModalState.markCompanyActive,
          });
        }}
        okText="入金確認を登録"
        okButtonProps={{ loading: getMutationPendingState(confirmPaymentMutation), 'data-testid': 'confirm-modal-ok' } as TestableButtonProps}
        cancelText="キャンセル"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {formatCompanyDisplay(confirmModalState.contract?.company)}
          </Text>
          <Text type="secondary">
            支払期限: {formatDate(confirmModalState.contract?.paymentDueDate)} / 請求額:{' '}
            {confirmModalState.contract ? formatCurrency(confirmModalState.contract.amount) : '-'}
          </Text>
          <Input.TextArea
            rows={3}
            placeholder="メモ (任意)"
            value={confirmModalState.note}
            onChange={(event) =>
              setConfirmModalState((prev) => ({ ...prev, note: event.target.value }))
            }
            maxLength={500}
          />
          <Checkbox
            checked={confirmModalState.markCompanyActive}
            onChange={(event) =>
              setConfirmModalState((prev) => ({
                ...prev,
                markCompanyActive: event.target.checked,
              }))
            }
          >
            会社のサブスクリプションを active に更新する
          </Checkbox>
        </Space>
      </Modal>

      <Modal
        title={reminderModalState.contract ? `${reminderModalState.contract.company.name} への督促` : '督促'}
        open={reminderModalState.open}
        onCancel={() => setReminderModalState({ open: false, memo: '', method: 'email' })}
        onOk={() => {
          if (!reminderModalState.contract) {
            return;
          }
          sendReminderMutation.mutate({
            contractId: reminderModalState.contract.id,
            method: reminderModalState.method,
            memo: reminderModalState.memo || undefined,
          });
        }}
        okText="督促を記録"
        okButtonProps={{ loading: getMutationPendingState(sendReminderMutation) }}
        cancelText="キャンセル"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {formatCompanyDisplay(reminderModalState.contract?.company)}
          </Text>
          <Select<ReminderMethod>
            value={reminderModalState.method}
            onChange={(value) =>
              setReminderModalState((prev) => ({
                ...prev,
                method: value,
              }))
            }
          >
            {REMINDER_METHOD_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                <Space>
                  {option.icon}
                  {option.label}
                </Space>
              </Option>
            ))}
          </Select>
          <Input.TextArea
            rows={3}
            placeholder="連絡内容・備考 (任意)"
            value={reminderModalState.memo}
            onChange={(event) =>
              setReminderModalState((prev) => ({
                ...prev,
                memo: event.target.value,
              }))
            }
            maxLength={500}
          />
          <Alert
            type="info"
            showIcon
            message="注意"
            description="送信したメールや電話の詳細をメモとして残してください。"
          />
        </Space>
      </Modal>

      <CreateBankTransferTestModal
        open={createTestModalOpen}
        onClose={() => setCreateTestModalOpen(false)}
        onSuccess={() => {
          setCreateTestModalOpen(false);
          void invalidateContracts();
        }}
      />
    </Space>
  );
};

export default BankTransferManagementPage;







